import React from "react";
import { Controller } from "react-hook-form";
import {
    TextField,
    Autocomplete,
    FormControl,
    Chip,
    CircularProgress,
} from "@mui/material";
import { buildValidationRules, getReadOnlyStyles, HELPER_TEXT_STYLES } from "../fieldHelpers";

/**
 * Renders a dropdown/select field with single or multiple selection
 *
 * @param {Object} props - Component props
 * @param {Object} props.field - Field configuration
 * @param {string} props.field.name - Field name (used as form key)
 * @param {string} props.field.label - Field label
 * @param {Array} props.field.options - Array of {label, value} options
 * @param {string} props.field.placeholder - Placeholder text
 * @param {boolean} props.field.required - Whether field is required
 * @param {boolean} props.field.multiple - Whether multiple selection is allowed
 * @param {boolean} props.field.fullWidth - Whether field takes full width
 * @param {boolean} props.field.disabled - Whether field is disabled
 * @param {boolean} props.field.readOnly - Whether field is readonly
 * @param {string} props.field.errorMessage - Custom error message
 * @param {Function} [props.field.onChange] - Optional external onChange (used by pages to update reducers)
 * @param {boolean} [props.field.isLoading] - Shows spinner inside the dropdown when true
 * @param {Object} props.control - React Hook Form control object
 * @returns {JSX.Element} Rendered dropdown field
 */
const DropdownFieldRenderer = ({ field, control }) => {
    const {
        name,
        label,
        options = [],
        placeholder = "",
        required = false,
        multiple = false,
        fullWidth = true,
        disabled = false,
        readOnly = false,
        onChange: externalOnChange,
        isLoading = false,
    } = field;

    const rules = buildValidationRules(field);

    return (
        <Controller
            key={name}
            name={name}
            control={control}
            rules={rules}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
                <FormControl fullWidth={fullWidth} required={required} disabled={disabled} error={!!error}>
                    <Autocomplete
                        id={name}
                        multiple={multiple}
                        value={
                            multiple
                                ? options.filter((opt) => (value || []).includes(opt.value))
                                : options.find((opt) => opt.value === value) || null
                        }
                        onChange={(event, newValue) => {
                            let normalizedValue;

                            if (multiple) {
                                normalizedValue = newValue ? newValue.map((item) => item.value) : [];
                            } else {
                                normalizedValue = newValue?.value || "";
                            }

                            onChange(normalizedValue);

                            if (typeof externalOnChange === "function") {
                                externalOnChange(normalizedValue);
                            }
                        }}
                        options={options}
                        getOptionLabel={(option) => option.label}
                        isOptionEqualToValue={(option, val) => option.value === val.value}
                        readOnly={readOnly}
                        disabled={disabled || readOnly}
                        loading={isLoading}
                        filterOptions={(options, params) => {
                            const filtered = options.filter((option) => {
                                if (multiple) {
                                    return !(value || []).includes(option.value);
                                }
                                return true;
                            });

                            const { inputValue } = params;
                            if (inputValue) {
                                return filtered.filter((option) =>
                                    option.label.toLowerCase().includes(inputValue.toLowerCase())
                                );
                            }

                            return filtered;
                        }}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label={label}
                                required={required}
                                error={!!error}
                                placeholder={
                                    multiple && (!value || value.length === 0)
                                        ? placeholder || "Select options..."
                                        : placeholder || ""
                                }
                                InputProps={{
                                    ...params.InputProps,
                                    readOnly: readOnly,
                                    sx: getReadOnlyStyles(readOnly),
                                    endAdornment: (
                                        <>
                                            {isLoading ? (
                                                <CircularProgress color="inherit" size={18} sx={{ mr: 1 }} />
                                            ) : null}
                                            {params.InputProps.endAdornment}
                                        </>
                                    ),
                                }}
                                FormHelperTextProps={{
                                    sx: HELPER_TEXT_STYLES,
                                }}
                                helperText={error?.message}
                            />
                        )}
                        renderTags={(tagValue, getTagProps) =>
                            tagValue.map((option, index) => (
                                <Chip
                                    key={option.value}
                                    label={option.label}
                                    {...getTagProps({ index })}
                                    color="primary"
                                    variant="outlined"
                                    size="small"
                                />
                            ))
                        }
                        disableCloseOnSelect={multiple}
                    />
                </FormControl>
            )}
        />
    );
};

export default DropdownFieldRenderer;
